/* eslint-disable no-magic-numbers */

import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client/react';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import {
  Door,
  DoorBatteryWarningLevel,
  LocationInput,
  Mutation,
  MutationAddLocationArgs,
  MutationDeleteDoorByIdArgs,
  MutationDeleteLocationArgs,
  MutationUpdateDoorArgs,
  MutationUpdateLocationArgs,
  Query
} from 'graphql/generated/graphqlTypes';
import {
  ADD_LOCATION,
  DELETE_DOOR_BY_ID,
  DELETE_LOCATION,
  UPDATE_DOOR_WITH_LOCATIONS,
  UPDATE_LOCATION
} from 'graphql/mutations';
import { GET_DOORS_FOR_TENANT_WITH_LOCATIONS, GET_LOCATIONS_WITH_DOORS_FOR_TENANT } from 'graphql/queries';
import { useEffect } from 'react';
import {
  deleteDoorWithLocationsAction,
  setDoorWithLocationsAction,
  updateDoorWithLocationsAction
} from 'state/actions/doorsWithLocations';
import {
  addLocationsWithDoorsAction,
  deleteLocationsWithDoorsAction,
  setLocationsWithDoorsAction,
  updateLocationsWithDoorsAction
} from 'state/actions/locationsWithDoors';
import { doorsWithLocationsVar, locationsAndDoorsRefetchVar, locationsWithDoorsVar } from 'state/vars';
import ArrayUtil from 'utils/Array/Array.util';
import { ignoreHandledMutationError } from 'utils/Mutation/ignoreHandledMutationError';
import { BaseHookProps } from '../shared/types';
dayjs.extend(weekday);

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_DOORS_FOR_TENANT_WITH_LOCATIONS_TYPED: TypedDocumentNode<Pick<Query, 'getDoorsForTenant'>> =
  GET_DOORS_FOR_TENANT_WITH_LOCATIONS;
const GET_LOCATIONS_WITH_DOORS_FOR_TENANT_TYPED: TypedDocumentNode<Pick<Query, 'getLocationsForTenant'>> =
  GET_LOCATIONS_WITH_DOORS_FOR_TENANT;
const UPDATE_DOOR_WITH_LOCATIONS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'updateDoor'>,
  MutationUpdateDoorArgs
> = UPDATE_DOOR_WITH_LOCATIONS;
const DELETE_DOOR_BY_ID_TYPED: TypedDocumentNode<
  Pick<Mutation, 'deleteDoorById'>,
  MutationDeleteDoorByIdArgs
> = DELETE_DOOR_BY_ID;
const ADD_LOCATION_TYPED: TypedDocumentNode<Pick<Mutation, 'addLocation'>, MutationAddLocationArgs> = ADD_LOCATION;
const UPDATE_LOCATION_TYPED: TypedDocumentNode<
  Pick<Mutation, 'updateLocation'>,
  MutationUpdateLocationArgs
> = UPDATE_LOCATION;
const DELETE_LOCATION_TYPED: TypedDocumentNode<
  Pick<Mutation, 'deleteLocation'>,
  MutationDeleteLocationArgs
> = DELETE_LOCATION;

const overwriteBatteryStatus = (doors: Door[]) =>
  doors.map((door) => {
    const firstTwoChars = door.hardwareIdentifier?.slice(0, 2);
    switch (firstTwoChars) {
      case '65':
      case '66':
        return { ...door, batteryWarningLevel: DoorBatteryWarningLevel.Powered };
      default:
        return door;
    }
  });

const useDoorsAndLocations = ({ handleFetchError }: BaseHookProps) => {
  const doorsWithLocations = useReactiveVar(doorsWithLocationsVar);
  const locationsWithDoors = useReactiveVar(locationsWithDoorsVar);
  const locationsAndDoorsRefetch = useReactiveVar(locationsAndDoorsRefetchVar);

  const getLocationsOfDoor = (doorId: string) =>
    locationsWithDoors.filter((location) => location.doors.some((door) => door.id === doorId));

  const [getDoorsWithLocationsForTenant, { loading: doorsWithLocationsLoading }] = useLazyQuery(
    GET_DOORS_FOR_TENANT_WITH_LOCATIONS_TYPED,
    { fetchPolicy: 'network-only' }
  );

  const fetchDoorsWithLocationsForTenant = async () => {
    try {
      const { data } = await getDoorsWithLocationsForTenant();
      if (data) {
        const doors = overwriteBatteryStatus(data.getDoorsForTenant);
        const doorsComplete = doors.map(
          (door): Door => ({
            ...door,
            locations: door.locations.map((location) => ({
              ...location,
              doors: doors.filter((locationDoor) =>
                locationDoor.locations.some((doorLocation) => doorLocation.id === location.id)
              )
            }))
          })
        );
        setDoorWithLocationsAction(doorsComplete);
      }
    } catch {
      handleFetchError('Error while fetching doors');
    }
  };

  useEffect(() => {
    void fetchDoorsWithLocationsForTenant();
  }, [getDoorsWithLocationsForTenant]);

  const [getLocationsWithDoorsForTenant, { loading: locationsWithDoorsLoading }] = useLazyQuery(
    GET_LOCATIONS_WITH_DOORS_FOR_TENANT_TYPED,
    { fetchPolicy: 'network-only' }
  );

  const fetchLocationsWithDoorsForTenant = async () => {
    try {
      const { data } = await getLocationsWithDoorsForTenant();
      if (data) {
        setLocationsWithDoorsAction(data.getLocationsForTenant);
      }
    } catch {
      handleFetchError('Error while fetching locations');
    }
  };

  useEffect(() => {
    void fetchLocationsWithDoorsForTenant();
  }, [getLocationsWithDoorsForTenant]);

  useEffect(() => {
    if (locationsAndDoorsRefetch === true) {
      void fetchDoorsWithLocationsForTenant();
      void fetchLocationsWithDoorsForTenant();
      locationsAndDoorsRefetchVar(false);
    }
  }, [getDoorsWithLocationsForTenant, getLocationsWithDoorsForTenant, locationsAndDoorsRefetch]);

  const [updateDoorWithLocationsMutation, { loading: updateDoorWithLocationsLoading }] = useMutation(
    UPDATE_DOOR_WITH_LOCATIONS_TYPED,
    {
      onCompleted: (data) => {
        if (data && data.updateDoor) {
          const updatedDoor = overwriteBatteryStatus([data.updateDoor])[0];
          const updateDoorComplete: Door = {
            ...updatedDoor,
            locations: updatedDoor.locations.map((location) => ({
              ...location,
              doors: []
            }))
          };
          updateDoorWithLocationsAction(updateDoorComplete);
        }
        locationsAndDoorsRefetchVar(true);
      },
      onError: () => {
        handleFetchError('Error while updating doors');
      }
    }
  );

  const [deleteDoorWithLocationsMutation, { loading: deleteDoorWithLocationsLoading }] = useMutation(
    DELETE_DOOR_BY_ID_TYPED,
    {
      onCompleted: (data) => {
        if (data.deleteDoorById) {
          deleteDoorWithLocationsAction(data.deleteDoorById);
        }
        locationsAndDoorsRefetchVar(true);
      },
      onError: () => {
        handleFetchError('Error while deleting doors');
      }
    }
  );
  const updateDoorWithLocations = async (
    doorId: string,
    name: string,
    locationIds?: string[] | null,
    officeModeFrom?: string,
    officeModeTo?: string
  ) => {
    // Failure is already handled in the mutation's onError, so the rejection is ignored here.
    const { data } = await ignoreHandledMutationError(
      updateDoorWithLocationsMutation({
        variables: {
          doorId,
          name,
          locationIds,
          officeMode: {
            scheduleType: 'weekSchedule',
            weekSchedule:
              officeModeFrom && officeModeTo
                ? ArrayUtil.Range(7).map((_, index) => ({
                    dayName: dayjs().weekday(index).format('dddd').toLowerCase(),
                    from: officeModeFrom,
                    to: officeModeTo
                  }))
                : []
          }
        }
      })
    );
    return data;
  };

  const deleteDoor = async (doorId: string) => {
    const { data } = await deleteDoorWithLocationsMutation({
      variables: {
        doorId
      }
    });
    return data;
  };

  const isOfficeMode = (door: Door) => door.officeMode.weekDays.some((day) => day !== null);

  const [addLocationMutation, { loading: addLocationLoading }] = useMutation(ADD_LOCATION_TYPED, {
    onCompleted: (data) => {
      addLocationsWithDoorsAction(data.addLocation);
      locationsAndDoorsRefetchVar(true);
    },
    onError: () => {
      handleFetchError('Error while adding location');
    }
  });

  const [updateLocationMutation, { loading: updateLocationLoading }] = useMutation(UPDATE_LOCATION_TYPED, {
    onCompleted: (data) => {
      if (data && data.updateLocation) updateLocationsWithDoorsAction(data.updateLocation);
      locationsAndDoorsRefetchVar(true);
    },

    onError: () => {
      handleFetchError('Error while updating location');
    }
  });

  const [deleteLocationMutation, { loading: deleteLocationLoading }] = useMutation(DELETE_LOCATION_TYPED, {
    onCompleted: (data) => {
      if (data.deleteLocation) {
        deleteLocationsWithDoorsAction(data.deleteLocation.id);
      }
      locationsAndDoorsRefetchVar(true);
    },
    onError: () => {
      handleFetchError('Error while deleting location');
    }
  });

  const addLocation = async (location: LocationInput) => {
    // Failure is already handled in the mutation's onError, so the rejection is ignored here.
    const { data } = await ignoreHandledMutationError(
      addLocationMutation({
        variables: {
          location
        }
      })
    );
    return data;
  };

  const updateLocation = async (id: string, location: LocationInput) => {
    // Failure is already handled in the mutation's onError, so the rejection is ignored here.
    const { data } = await ignoreHandledMutationError(
      updateLocationMutation({
        variables: {
          id,
          location
        }
      })
    );
    return data;
  };

  const deleteLocation = async (id: string) => {
    // Failure is already handled in the mutation's onError, so the rejection is ignored here.
    const { data } = await ignoreHandledMutationError(
      deleteLocationMutation({
        variables: {
          id
        }
      })
    );
    return data;
  };

  return {
    doorsWithLocations,
    doorsWithLocationsLoading,
    getLocationsWithDoorsForTenant,
    getDoorsWithLocationsForTenant,
    updateDoorWithLocations,
    updateDoorWithLocationsLoading,
    deleteDoor,
    deleteDoorWithLocationsLoading,
    isOfficeMode,
    locationsWithDoors,
    locationsWithDoorsLoading,
    getLocationsOfDoor,
    addLocation,
    addLocationLoading,
    updateLocation,
    updateLocationLoading,
    deleteLocation,
    deleteLocationLoading
  };
};

export default useDoorsAndLocations;
